import { useCallback, useEffect, useRef, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import PropTypes from 'prop-types';

import { IconButton } from 'basic-components';
import { Clear, Minimize } from 'basic-components/icons';
import { globalActions } from 'primary-components';

import { builderPipelineCreatorActions } from 'actions/modules/datascience';
import dsHooks from 'hooks/modules/datascience';

import classNames from 'classnames';
import './ExpandedComment.scss';

const ExpandedComment = ({ commentId, engine, node, setVisible }) => {
  const dispatch = useDispatch(),
        [focused, setFocus] = useState(false),
        text = useSelector(state => state.datascienceState.creatorState.builderState.comments.find(c => c.id === commentId)?.text),
        executing = useSelector(state => state.datascienceState.creatorState.basicState.executing),
        isBlockedByWarehouse = dsHooks.useIsBlockedByWarehouseAccess(),
        disabled = executing || isBlockedByWarehouse,
        author = useSelector(state => state.datascienceState.creatorState.builderState.comments.find(c => c.id === commentId)?.author || "Comment"),
        changeCommentText = useCallback(e => {
          if(disabled) {
            return;
          }
          return dispatch(builderPipelineCreatorActions.changeComment(e.target.value, "text", commentId))
        }, [disabled, dispatch, commentId]),
        removeComment = useCallback(() => {
          if(disabled) {
            return;
          }
          dispatch(globalActions.getToastsStore()).info(
            "Are you sure you want to delete this comment?",
            "Delete comment",
            {
              withCancel: false,
              confirmText: "Delete",
              confirmAction: () => {
                dispatch(builderPipelineCreatorActions.removeComment(commentId))
                engine.model.removeNode(node.options.id)
                engine.repaintCanvas()
              },
            }
          );
        }, [commentId, engine, node, disabled, dispatch]),
        [textAreaStyle, setTextAreaStyle] = useState(() => ({ height: "16px" })),
        textAreaRef = useRef(),
        [dragging, setDragging] = useState(false),
        stopPropagation = useCallback(e => e.stopPropagation(), []),
        onFocus = useCallback(() => !disabled && setFocus(true), [disabled]),
        onBlur = useCallback(() => !disabled && setFocus(false), [disabled]),
        setDraggingTrue = useCallback(() => !disabled && setDragging(true), [disabled]),
        setDraggingFalse = useCallback(() => !disabled && setDragging(false), [disabled]),
        number = useSelector(state => state.datascienceState.creatorState.builderState.comments.findIndex(c => c.id === commentId) + 1)

  useEffect(() => {
    setTextAreaStyle(textAreaStyle => textAreaStyle.height !== "auto" ? { height: "auto" } : textAreaStyle)
  }, [text])

  useEffect(() => {
    if(textAreaStyle.height === "auto") {
      setTextAreaStyle({
        height: textAreaRef.current?.scrollHeight
      })
    }
  }, [textAreaStyle])
  return (
    <div 
        className={classNames("data-science__pipeline-creator__comments__comment__expanded", { "data-science__pipeline-creator__comments__comment__expanded--is-dragging": dragging })}
        onMouseDown={setDraggingTrue}
        onMouseUp={setDraggingFalse}
    >
      <div className="data-science__pipeline-creator__comments__comment__expanded__author">
        {author}
      </div>
      <div className="data-science__pipeline-creator__comments__comment__expanded__text">
        <textarea
            disabled={disabled}
            maxLength={1000}
            onBlur={onBlur}
            onChange={changeCommentText}
            onFocus={onFocus}
            onMouseDown={stopPropagation}
            onMouseUp={stopPropagation}
            placeholder="Your comment..."
            ref={textAreaRef}
            style={textAreaStyle}
            value={text}
        />
      </div>
      <div className={classNames("data-science__pipeline-creator__comments__comment__expanded__counter", { "data-science__pipeline-creator__comments__comment__expanded__counter--is-hidden": !focused })}>
        {1000 - String(text).length}
      </div>
      <IconButton 
          className="data-science__pipeline-creator__comments__comment__expanded__minimize"
          onClick={setVisible}
      >
        <Minimize/>
      </IconButton>
      <IconButton 
          className={classNames("data-science__pipeline-creator__comments__comment__expanded__remove", { "data-science__pipeline-creator__comments__comment__expanded__remove--is-disabled": disabled })}
          onClick={removeComment}
      >
        <Clear/>
      </IconButton>
      <div className="data-science__pipeline-creator__comments__comment__expanded__number">
        <div className="data-science__pipeline-creator__comments__comment__expanded__number__inner">
          {number}
        </div>
      </div>
    </div>
  )
}

ExpandedComment.propTypes = {
  commentId: PropTypes.string.isRequired,
  engine: PropTypes.object.isRequired,
  node: PropTypes.object.isRequired,
  setVisible: PropTypes.func.isRequired
}

export default ExpandedComment;